import * as yup from 'yup';
import { sportRegex } from '../utils/regex/sport-regex';
import { normalizeDateValue } from '../utils/helper/normalizeDateValue';

const today = new Date();

// const leagueAndTeamRegex = (
//   fieldName: string,
//   value?: string | null
// ): boolean | ValidationError => {
//   if (!value) return true;
//   if (value.length < 2)
//     return new yup.ValidationError(
//       `${fieldName} must be at least 2 characters long.`
//     );
//   if (value.length > 50)
//     return new yup.ValidationError(
//       `${fieldName} must be at most 50 characters long.`
//     );
//   if (!/^[A-Za-z\s]+$/.test(value))
//     return new yup.ValidationError(
//       `${fieldName} can only contain letters and spaces.`
//     );
//   return true;
// };

export const sportBackgroundSchema = yup.object().shape({
  description: yup
    .string()
    .max(500, 'File description must be between 0 and 500 characters.'),
  links: yup.array().of(yup.string().url('Each link must be a valid URL.')),
});

export const sportSchema = yup.object().shape({
  sportIds: yup
    .array()
    .of(yup.string().required('Sport is required'))
    .min(1, 'At least one sport is required')
    .required('Sports are required'),
});

//eslint-disable-next-line
export const createTeamSchema = (isOtherSport: boolean) => {
  return yup.object().shape({
    isIndividualSport: yup
      .boolean()
      .required('isIndividualSport is required')
      .default(false),
    leagueId: yup.string().nullable(),
    teamId: yup.string().nullable(),
    leagueName: yup
      .string()
      .required('League is required')
      .transform((value: string) => value.replace(/\s+/g, ' ').trim())
      .test('format-when-present', function (value) {
        if (!value || value.length === 0) {
          return true; // Let required handle empty values
        }
        if (!sportRegex.leagueNameRegex.test(value)) {
          return this.createError({
            message:
              'The league name can only contain letters, numbers, spaces, underscores, hyphens, dots, apostrophes, and question marks.',
          });
        }
        if (value.length < 2) {
          return this.createError({
            message: 'The league name must be at least 2 characters long.',
          });
        }
        if (value.length > 80) {
          return this.createError({
            message: 'The league name must be at most 80 characters long.',
          });
        }
        return true;
      }),
    teamName: yup
      .string()
      .nullable()
      .when('isIndividualSport', {
        is: false,
        then: (schema) =>
          schema
            .transform((value: string) => value.replace(/\s+/g, ' ').trim())
            .required('Team is required')
            .transform((value: string | null) =>
              value ? value.replace(/\s+/g, ' ').trim() : value
            )
            .matches(sportRegex.teamNameRegex, {
              message:
                'The team name can only contain letters, numbers, spaces, underscores, hyphens, dots, apostrophes, and question marks.',
            })
            .min(2, 'The team name must be at least 2 characters long.')
            .max(80, 'The team name must be at most 80 characters long.'),
        otherwise: (schema) => schema.nullable().default(null),
      }),
    isActiveTeam: yup.boolean().default(false),
    startDateInTeam: yup
      .date()
      .transform((value, originalValue) =>
        normalizeDateValue(value, originalValue)
      )
      .typeError('Start career date in team must be a valid date')
      .required('Start career date in team is required')
      .max(today, 'Start career date in team cannot be in the future'),
    endDateInTeam: yup
      .date()
      .nullable()
      .transform((value, originalValue) =>
        normalizeDateValue(value, originalValue)
      )
      .typeError('End career date in team must be a valid date')
      .when('isActiveTeam', {
        is: true,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) =>
          schema
            .required('End career date in team is required')
            .max(today, 'End career date in team cannot be in the future')
            .min(
              yup.ref('startDateInTeam'),
              'End career date in team must be greater than start career in team date'
            )
            .test(
              'is-greater',
              'End career date in team must be greater than start career date in team ',
              function (value: Date) {
                const { startDateInTeam } = this.parent as any;
                if (!value || !startDateInTeam) return true;
                return value > startDateInTeam;
              }
            ),
      }),
  });
};

export const createSportBackgroundSchema = (isOtherSport: boolean) => {
  const sportName = isOtherSport
    ? yup
        .string()
        .required('Sport name is required')
        .min(2, 'Sport should be at least 2 characters long.')
        .max(70, 'Sport should be at most 70 characters long.')
    : yup.string().nullable();

  return yup.object().shape({
    isOtherSport: yup.boolean().default(false),
    isActiveSport: yup.boolean().default(false),
    sportName: sportName,
    startCareerDate: yup
      .date()
      .transform((value, originalValue) =>
        normalizeDateValue(value, originalValue)
      )
      .typeError('Start career date must be a valid date')
      .required('Start career date is required')
      .max(today, 'Start career date cannot be in the future'),
    endCareerDate: yup
      .date()
      .nullable()
      .transform((value, originalValue) =>
        normalizeDateValue(value, originalValue)
      )
      .typeError('End career date must be a valid date')
      .when('isActiveSport', {
        is: true,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) =>
          schema
            .required('End career date is required')
            .max(today, 'End career date cannot be in the future')
            .min(
              yup.ref('startCareerDate'),
              'End career date must be greater than start career date'
            )
            .test(
              'is-greater',
              'End career date must be greater than start career date',
              function (value: Date) {
                const { startCareerDate } = this.parent as any;
                if (!value || !startCareerDate) return true;
                return value > startCareerDate;
              }
            ),
      }),

    description: yup
      .string()
      .nullable()
      .notRequired()
      .min(10, 'Description should be at least 10 characters long.')
      .max(500, 'Description should be at most 500 characters long.'),

    fileData: yup.array().of(sportBackgroundSchema),

    teams: yup
      .array()
      .of(createTeamSchema(isOtherSport))
      .min(1, 'At least one team is required')
      .when('isOtherSport', {
        is: true,
        then: (schema) => schema.nullable().default(null),
        otherwise: (schema) => schema.required('Teams are required'),
      }),
  });
};
